/**
 * US Eastern (America/New_York) for market data display and hours checks.
 *
 * Server contract (Option Flow websocket `swps` rows):
 * `date` (YYYY-MM-DD) + `time` (HH:mm:ss) are US Eastern wall-clock values
 * stored by the backend — not UTC and not the user's local timezone.
 */

const US_EASTERN = 'America/New_York';

const easternWallClockFormatter24 = new Intl.DateTimeFormat('en-US', {
  timeZone: US_EASTERN,
  month: 'numeric',
  day: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
});

const easternWallClockFormatter12 = new Intl.DateTimeFormat('en-US', {
  timeZone: US_EASTERN,
  month: 'numeric',
  day: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
  hour12: true,
});

const easternTimeOnlyFormatter12 = new Intl.DateTimeFormat('en-US', {
  timeZone: US_EASTERN,
  hour: 'numeric',
  minute: '2-digit',
  hour12: true,
});

const easternNowPartsFormatter = new Intl.DateTimeFormat('en-US', {
  timeZone: US_EASTERN,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  weekday: 'short',
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
});

function parseGmtOffset(value) {
  const gmt = value.match(/GMT([+-])(\d{1,2})(?::(\d{2}))?/);
  if (!gmt) return null;
  const sign = gmt[1] === '+' ? 1 : -1;
  const hours = parseInt(gmt[2], 10);
  const minutes = gmt[3] ? parseInt(gmt[3], 10) : 0;
  return sign * (hours + minutes / 60);
}

function nthSundayOfMonth(year, monthIndex, n) {
  const first = new Date(year, monthIndex, 1);
  const firstSunday = 1 + ((7 - first.getDay()) % 7);
  return new Date(year, monthIndex, firstSunday + 7 * (n - 1), 2, 0, 0);
}

function isUsEasternDstByRule(date) {
  const year = date.getFullYear();
  const dstStart = nthSundayOfMonth(year, 2, 2);
  const dstEnd = nthSundayOfMonth(year, 10, 1);
  return date >= dstStart && date < dstEnd;
}

function partValue(parts, type) {
  return parts.find(p => p.type === type)?.value ?? '';
}

function pad2(n) {
  return String(n).padStart(2, '0');
}

const WEEKDAY_TO_INDEX = {
  Sun: 0,
  Mon: 1,
  Tue: 2,
  Wed: 3,
  Thu: 4,
  Fri: 5,
  Sat: 6,
};

/**
 * UTC offset in hours (EST = -5, EDT = -4).
 */
export function getUsEasternUtcOffsetHours(date = new Date()) {
  try {
    const dtf = new Intl.DateTimeFormat('en-US', {
      timeZone: US_EASTERN,
      timeZoneName: 'shortOffset',
    });
    const tzPart = dtf.formatToParts(date).find(p => p.type === 'timeZoneName');
    const parsed = parseGmtOffset(tzPart?.value ?? '');
    if (parsed !== null) return parsed;
    const label = tzPart?.value ?? '';
    if (label.includes('EDT')) return -4;
    if (label.includes('EST')) return -5;
  } catch (e) {
    // Intl unavailable — fall through to rule-based DST
  }
  return isUsEasternDstByRule(date) ? -4 : -5;
}

export function getUsEasternDisplayOffsetHours(date = new Date()) {
  return -getUsEasternUtcOffsetHours(date);
}

export function getUsEasternOffsetMinutes(date = new Date()) {
  return getUsEasternUtcOffsetHours(date) * 60;
}

/**
 * Parse YYYY-MM-DD + HH:mm(:ss) as US Eastern wall-clock → UTC instant.
 */
export function parseEasternWallClock(dateStr, timeStr) {
  if (!dateStr || !timeStr) return null;

  const [y, m, d] = dateStr.split(/[-/]/).map(Number);
  const [hh, mm, ss = 0] = timeStr.split(':').map(Number);
  if (!y || !m || !d) return null;

  const roughUtc = Date.UTC(y, m - 1, d, hh, mm, ss);
  const offsetHours = getUsEasternUtcOffsetHours(new Date(roughUtc));
  return new Date(roughUtc - offsetHours * 3600000);
}

/** Parse flow row date/time (Eastern wall-clock). */
export function parseFlowTimestamp(date, time) {
  return parseEasternWallClock(date, time);
}

/** Parse UTC date + time strings → instant. */
export function parseUtcDateTime(dateStr, timeStr) {
  if (!dateStr || !timeStr) return null;
  const normalizedTime =
    timeStr.length === 5 ? `${timeStr}:00` : timeStr;
  const instant = new Date(`${dateStr}T${normalizedTime}Z`);
  return Number.isNaN(instant.getTime()) ? null : instant;
}

export function formatInstantEastern24(instant) {
  if (!instant || Number.isNaN(instant.getTime())) return '';
  const parts = easternWallClockFormatter24.formatToParts(instant);
  const month = partValue(parts, 'month');
  const day = partValue(parts, 'day');
  const hour = partValue(parts, 'hour');
  const minute = partValue(parts, 'minute');
  return `${month}/${day} ${pad2(hour)}:${pad2(minute)}`;
}

export function formatInstantEastern12(instant) {
  if (!instant || Number.isNaN(instant.getTime())) return '';
  const parts = easternWallClockFormatter12.formatToParts(instant);
  const month = partValue(parts, 'month');
  const day = partValue(parts, 'day');
  const hour = partValue(parts, 'hour');
  const minute = partValue(parts, 'minute');
  const dayPeriod = partValue(parts, 'dayPeriod');
  return `${month}/${day} ${hour}:${minute} ${dayPeriod}`;
}

export function formatInstantEasternTime12(instant) {
  if (!instant || Number.isNaN(instant.getTime())) return '';
  const parts = easternTimeOnlyFormatter12.formatToParts(instant);
  const hour = partValue(parts, 'hour');
  const minute = partValue(parts, 'minute');
  const dayPeriod = partValue(parts, 'dayPeriod');
  return `${hour}:${minute} ${dayPeriod}`;
}

/** Flow TIME column: M/D HH:mm in US Eastern. */
export function formatFlowDateTime24(date, time) {
  return formatInstantEastern24(parseFlowTimestamp(date, time));
}

/** 12-hour flow/scanner display in US Eastern. */
export function formatFlowDateTime12(date, time) {
  return formatInstantEastern12(parseFlowTimestamp(date, time));
}

/** @deprecated Use parseFlowTimestamp — kept for imports */
export function parseServerDateTimeToEastern(date, time) {
  return parseFlowTimestamp(date, time);
}

export function getEasternNowParts(now = new Date()) {
  const parts = easternNowPartsFormatter.formatToParts(now);
  const weekday = partValue(parts, 'weekday');
  return {
    year: parseInt(partValue(parts, 'year'), 10),
    month: parseInt(partValue(parts, 'month'), 10),
    day: parseInt(partValue(parts, 'day'), 10),
    hour: parseInt(partValue(parts, 'hour'), 10),
    minute: parseInt(partValue(parts, 'minute'), 10),
    dayOfWeek: WEEKDAY_TO_INDEX[weekday] ?? 0,
  };
}

/**
 * Calendar "today" in US Eastern as local Date at midnight (y, m, d from Eastern).
 */
export function getEasternCalendarDate(now = new Date()) {
  const { year, month, day } = getEasternNowParts(now);
  return new Date(year, month - 1, day);
}

/** @deprecated */
export function getUsEasternNow(date = new Date()) {
  const { year, month, day, hour, minute } = getEasternNowParts(date);
  return new Date(year, month - 1, day, hour, minute, 0, 0);
}

export function isUsMarketOpen(now = new Date()) {
  const { hour, minute, dayOfWeek } = getEasternNowParts(now);
  if (dayOfWeek === 0 || dayOfWeek === 6) return false;
  if (hour < 9 || hour >= 16) return false;
  if (hour === 9 && minute < 30) return false;
  return true;
}

export function isUsBmo(now = new Date()) {
  return getEasternNowParts(now).hour < 9;
}

export function isUsAmc(now = new Date()) {
  return getEasternNowParts(now).hour > 16;
}

export function isUsWeekend(now = new Date()) {
  const dow = getEasternNowParts(now).dayOfWeek;
  return dow === 0 || dow === 6;
}
